import React, { useState, useContext, useEffect } from "react";
import {
  Container,
  Segment,
  Form,
  Button,
  Grid,
  Image,
  Message,
  Icon,
  Checkbox,
  Header,
  Divider
} from "semantic-ui-react";
import { useNavigate, Link } from "react-router-dom";
import { signInUser, db, auth } from "../firebase/firebaseConfig";
import { signOut } from "firebase/auth";
import { startSession } from "../components/session";
import { doc, getDoc } from "firebase/firestore";
import { UserContext } from "../context/UserContext";
import { forceCloseSession } from "../components/session";

const Login = () => {

  const navigate = useNavigate();
  const { setUsers } = useContext(UserContext);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});
  const [showForce, setShowForce] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);
  const [pendingUserData, setPendingUserData] = useState(null);

  useEffect(() => {
    const savedEmail = localStorage.getItem("rememberEmail");
    if (savedEmail) {
      setEmail(savedEmail);
      setRemember(true);
    }
  }, []);

  useEffect(() => {
    const expired = localStorage.getItem("sessionExpired");

    if (expired) {
      setError("Tu sesión expiró o fue cerrada.");
      localStorage.removeItem("sessionExpired");
    }
  }, []);

  const validate = () => {
    let newErrors = {};
    if (!email)
      newErrors.email = "Ingrese su email";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      newErrors.email = "Formato inválido";
    if (!password)
      newErrors.password = "Ingrese su contraseña";
    else if (password.length < 6)
      newErrors.password = "Mínimo 6 caracteres";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    setError("");
    setShowForce(false);

    if (!validate()) return;

    setLoading(true);

    const cerrarAccesoRechazado = async () => {
      setUsers(null);
      localStorage.removeItem("users");

      try {
        await signOut(auth());
      } catch (error) {
        console.error("Error cerrando sesión no autorizada:", error);
      }
    };

    try {
      // ==========================================================
      // 1. AUTENTICACIÓN FIREBASE
      // ==========================================================

      const response = await signInUser(email, password);
      const uid = response.user.uid;

      // ==========================================================
      // 2. CONSULTAR USUARIO FIRESTORE
      // ==========================================================

      const usuarioRef = doc(db, "usuarios", uid);
      const usuarioSnap = await getDoc(usuarioRef);

      if (!usuarioSnap.exists()) {
        await cerrarAccesoRechazado();

        setError(
          "Usuario autenticado, pero no está registrado en LogisticMed."
        );

        return;
      }

      const userData = usuarioSnap.data();

      // ==========================================================
      // 3. VALIDAR ESTADO DEL USUARIO
      // ==========================================================

      if (userData.activo === false) {
        await cerrarAccesoRechazado();

        setError(
          "Tu cuenta se encuentra desactivada. Contacta al administrador."
        );

        return;
      }

      // ==========================================================
      // 4. VALIDAR ROL Y EMPRESA
      // ==========================================================

      if (!userData.rol || !userData.emp_id) {
        await cerrarAccesoRechazado();

        setError(
          "Tu usuario no tiene un rol o empresa asignada."
        );

        return;
      }

      // ==========================================================
      // 5. REGISTRAR USUARIO EN CONTEXTO
      // ==========================================================

      setUsers(userData);

      localStorage.setItem(
        "users",
        JSON.stringify(userData)
      );

      if (remember) {
        localStorage.setItem("rememberEmail", email);
      } else {
        localStorage.removeItem("rememberEmail");
      }

      // ==========================================================
      // 6. CONTROL DE SESIONES SIMULTÁNEAS
      // ==========================================================

      try {
        await startSession(response.user, userData);
      } catch (err) {
        if (err.message === "SESSION_ALREADY_ACTIVE") {
          setError("Este usuario ya tiene una sesión activa.");

          setShowForce(true);
          setPendingUser(response.user);
          setPendingUserData(userData);

          return;
        }

        await cerrarAccesoRechazado();

        setError("Error al registrar la sesión.");

        return;
      }

      // ==========================================================
      // 7. INGRESAR AL SISTEMA
      // ==========================================================

      const lastPath =
        localStorage.getItem("lastPath") || "/home";

      navigate(lastPath);

    } catch (err) {
      console.error("Error iniciando sesión:", err);

      await cerrarAccesoRechazado();

      switch (err.code) {
        case "auth/user-not-found":
          setError("Usuario no existe");
          break;

        case "auth/wrong-password":
          setError("Contraseña incorrecta");
          break;

        case "auth/invalid-email":
          setError("Email inválido");
          break;

        case "auth/invalid-credential":
          setError("Correo o contraseña incorrectos");
          break;

        case "permission-denied":
        case "firestore/permission-denied":
          setError(
            "No tienes permisos para acceder a la información de tu usuario."
          );
          break;

        default:
          setError("Error al iniciar sesión");
          break;
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundImage: "url('./fondoLogin.jpg')",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        backgroundSize: "cover"
      }}
    >
      {/* overlay */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(0,0,0,0.45)"
        }}
      />
      <Container text style={{ position: "relative" }}>

        <Segment
          padded="very"
          raised
          style={{
            maxWidth: 420,
            margin: "auto",
            borderRadius: 12,
            backdropFilter: "blur(6px)"
          }}
        >
          {/* LOGO */}
          <Image
            src="../../logo.png"
            size="small"
            centered
            style={{ marginBottom: 15 }}
          />
          <Header
            as="h2"
            textAlign="center"
            color="teal"
          >
            <Icon name="user circle" />
            Iniciar Sesión
          </Header>
          <Divider />
          {error &&
            <Message negative icon>
              <Icon name="warning circle" />
              <Message.Content>
                {error}
              </Message.Content>
            </Message>
          }
          {showForce && (
            <Button
              fluid
              color="orange"
              style={{ marginTop: 10 }}
              onClick={async () => {
                try {
                  // 1) cerrar sesión activa en Firestore
                  await forceCloseSession(pendingUser);

                  // 2) crear nueva sesión
                  await startSession(pendingUser, pendingUserData);

                  // 3) limpiar estado
                  setShowForce(false);

                  // 4) navegar
                  navigate("/home");
                } catch (e) {
                  setError("No se pudo cerrar la sesión anterior.");
                }
              }}
            >
              Cerrar sesión anterior y continuar
            </Button>
          )}
          <Form
            onSubmit={onSubmit}
            loading={loading}
            size="large"
          >
            <Form.Input
              label="Email"
              placeholder="usuario@email.com"
              icon="mail"
              iconPosition="left"
              value={email}
              error={
                errors.email
                  ? { content: errors.email }
                  : null
              }
              onChange={(e) =>
                setEmail(e.target.value)
              }
            />
            <Form.Input
              label="Password"
              placeholder="Ingrese su contraseña"
              type={
                showPassword ? "text" : "password"
              }
              icon={
                <Icon
                  name={
                    showPassword
                      ? "eye slash"
                      : "eye"
                  }
                  link
                  onClick={() =>
                    setShowPassword(!showPassword)
                  }
                />
              }
              iconPosition="left"
              value={password}
              error={
                errors.password
                  ? {
                    content:
                      errors.password
                  }
                  : null
              }
              onChange={(e) =>
                setPassword(e.target.value)
              }
            />
            <Grid>
              <Grid.Row>
                <Grid.Column width={8}>
                  <Checkbox
                    label="Recordarme"
                    checked={remember}
                    onChange={(e, data) =>
                      setRemember(data.checked)
                    }
                  />
                </Grid.Column>
                <Grid.Column
                  width={8}
                  textAlign="right"
                >
                  <Link to="/recover">
                    ¿Olvidaste tu contraseña?
                  </Link>
                </Grid.Column>
              </Grid.Row>
            </Grid>
            <Divider hidden />
            <Button
              fluid
              color="teal"
              size="large"
              type="submit"
              icon
              labelPosition="right"
            >
              Ingresar
              <Icon name="sign-in" />
            </Button>
          </Form>
        </Segment>
      </Container>
    </div>
  );
};

export default Login;